import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Plus, Search, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { payments as api, settings as settingsApi, unmatchedPayments as unmatchedApi } from '@/api/endpoints'
import { Alert, EmptyState, Input, Select, Spinner } from '@/components/ui'
import Pagination from '@/components/ui/Pagination'
import { useAuth } from '@/store/auth'
import { cn, formatDate, formatMoney } from '@/lib/utils'
import { PaymentType, type PaymentListRow, type PaymentSource, type UnmatchedPaymentRow } from '@/types/api'

import AddPaymentModal from './AddPaymentModal'
import MatchUnmatchedModal from './MatchUnmatchedModal'
import OrderModal from './OrderModal'
import { BankImports, hasBankImports } from './BankImports'
import { BankIcon, bankByCode, bankLabel } from '@/features/settings/banks'
import { CenterFilter, useCenterFilter } from '@/features/centers/useCenters'

const SOURCE_STYLE: Record<string, string> = {
  manual: 'bg-slate-100 text-slate-600',
  receipt: 'bg-green-50 text-green-700',
}
/** Import z banky (Fio, Tatra banka, SLSP...) - ikonka banky a jej názov. */
const BANK_SOURCE_STYLE = 'bg-sky-50 text-sky-700'

/**
 * Zoznam platieb naprieč celou školou — kde vidno, čo prišlo, ku ktorej
 * objednávke to patrí (klient, symbol) a odkiaľ platba pochádza.
 *
 * Druhá záložka je front na ručné spárovanie: platby z importu z banky (Fio
 * Banka, výpis Tatra banky), ktoré sa nepodarilo priradiť automaticky (viď
 * `apps.reservations.payments.import_transactions`).
 *
 * Zápis novej platby aj jej zmazanie je vždy nad konkrétnou objednávkou
 * (`OrderModal`) — táto stránka je vstupný bod k nej, či už kliknutím na
 * existujúci riadok, cez „Pridať platbu“, alebo spárovaním nespárovanej
 * platby.
 */
export default function PaymentsPage() {
  const { t } = useTranslation()
  const readOnly = !useAuth((s) => s.user)?.can_write

  const [view, setView] = useState<'list' | 'unmatched' | 'import'>('list')
  // Import z banky - aj pre manažéra rezervácií (párovanie platieb je denná
  // práca). Nastavenie bánk ostáva v Nastaveniach → Integrácie pre správcu.
  const settings = useQuery({ queryKey: ['settings'], queryFn: settingsApi.get, enabled: !readOnly })
  const canImport = !readOnly && hasBankImports(settings.data)
  const [importError, setImportError] = useState<string | null>(null)
  const [open, setOpen] = useState<{ ref: number; clientName: string | null } | null>(null)

  const unmatchedCount = useQuery({
    queryKey: ['payments-unmatched-count'],
    queryFn: () => unmatchedApi.list({ limit: 1 }),
  })

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold">{t('paymentsPage.title')}</h1>
      </div>

      {/* Na mobile sa záložky posúvajú do strany - spodná čiara je na
          vnútornom bloku, aby ju posúvanie neorezalo. */}
      <nav className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0">
        <div className="flex min-w-max gap-1 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setView('list')}
          className={cn(
            '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium',
            view === 'list'
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-slate-500 hover:text-slate-700',
          )}
        >
          {t('paymentsPage.tabList')}
        </button>
        <button
          type="button"
          onClick={() => setView('unmatched')}
          className={cn(
            '-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium',
            view === 'unmatched'
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-slate-500 hover:text-slate-700',
          )}
        >
          {t('paymentsPage.tabUnmatched')}
          {unmatchedCount.data?.count ? (
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">
              {unmatchedCount.data.count}
            </span>
          ) : null}
        </button>
        {canImport ? (
          <button
            type="button"
            onClick={() => setView('import')}
            className={cn(
              '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium',
              view === 'import'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            {t('paymentsPage.tabImport')}
          </button>
        ) : null}
      </div>
      </nav>

      {view === 'list' ? (
        <ListSection readOnly={readOnly} onOpen={setOpen} />
      ) : view === 'import' && settings.data ? (
        <div className="max-w-3xl space-y-5">
          {importError ? <Alert variant="error">{importError}</Alert> : null}
          <BankImports settings={settings.data} onError={setImportError} />
        </div>
      ) : (
        <UnmatchedSection readOnly={readOnly} />
      )}

      {open ? (
        <OrderModal
          orderRef={open.ref}
          clientName={open.clientName}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  )
}

function ListSection({
  readOnly,
  onOpen,
}: {
  readOnly: boolean
  onOpen: (order: { ref: number; clientName: string | null }) => void
}) {
  const { t } = useTranslation()

  const [query, setQuery] = useState('')
  const [paymentType, setPaymentType] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [adding, setAdding] = useState(false)

  // Strediská (`sql/040`) - spoločný filter s kalendárom a štatistikami.
  const [centerIds] = useCenterFilter()
  const { data, isLoading } = useQuery({
    queryKey: ['payments', query, paymentType, page, pageSize, centerIds.join(',')],
    queryFn: () =>
      api.list({
        q: query || undefined,
        payment_type: paymentType || undefined,
        centers: centerIds.length ? centerIds.join(',') : undefined,
        page,
        limit: pageSize,
      }),
    placeholderData: keepPreviousData,
  })

  const totalPages = Math.max(1, Math.ceil((data?.count ?? 0) / pageSize))

  function openRow(row: PaymentListRow) {
    onOpen({ ref: row.ref, clientName: row.client_name })
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-400">{data?.count ?? 0}</span>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="w-56 pl-8"
              placeholder={t('paymentsPage.searchPlaceholder')}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setPage(1)
              }}
            />
          </div>

          <CenterFilter className="w-56" />
          <Select
            className="w-44"
            value={paymentType}
            onChange={(e) => {
              setPaymentType(e.target.value)
              setPage(1)
            }}
          >
            <option value="">{t('paymentsPage.allMethods')}</option>
            {[PaymentType.Cash, PaymentType.Card, PaymentType.Transfer, PaymentType.Invoice, PaymentType.Other].map(
              (p) => (
                <option key={p} value={p}>
                  {t(`reservation.payment.${p}`)}
                </option>
              ),
            )}
          </Select>

          {!readOnly ? (
            <button type="button" className="btn-primary whitespace-nowrap" onClick={() => setAdding(true)}>
              <Plus size={15} />
              {t('paymentsPage.add')}
            </button>
          ) : null}
        </div>
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Spinner className="h-5 w-5" />
          </div>
        ) : (data?.results.length ?? 0) === 0 ? (
          <EmptyState>{t('app.empty')}</EmptyState>
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>{t('changelog.created')}</th>
                <th>{t('paymentsPage.columnClient')}</th>
                <th>{t('reservation.paymentRef')}</th>
                <th>{t('paymentsPage.columnMethod')}</th>
                <th>{t('paymentsPage.columnSource')}</th>
                <th className="text-right">{t('paymentsPage.columnAmount')}</th>
              </tr>
            </thead>
            <tbody>
              {data?.results.map((row) => (
                <tr
                  key={row.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openRow(row)}
                >
                  <td>{formatDate(row.created)}</td>
                  <td className="font-medium">{row.client_name || '—'}</td>
                  <td className="font-mono text-slate-600">{row.ref}</td>
                  <td className="text-slate-600">
                    {t(`reservation.payment.${row.payment_type}`)}
                    {row.note ? <span className="ml-1.5 text-slate-400">· {row.note}</span> : null}
                    {row.ekasa_ref ? (
                      <span className="ml-1.5 font-mono text-xs text-slate-500">· eKasa {row.ekasa_ref}</span>
                    ) : null}
                  </td>
                  <td>
                    <SourceBadge source={row.source} />
                  </td>
                  <td className="text-right tabular-nums">{formatMoney(row.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <Pagination
          page={page}
          pages={totalPages}
          pageSize={pageSize}
          count={data?.count ?? 0}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      </div>

      {adding ? (
        <AddPaymentModal
          onClose={() => setAdding(false)}
          onPicked={(ref, clientName) => {
            setAdding(false)
            onOpen({ ref, clientName })
          }}
        />
      ) : null}
    </div>
  )
}

function UnmatchedSection({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [matching, setMatching] = useState<UnmatchedPaymentRow | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['payments-unmatched'],
    queryFn: () => unmatchedApi.list({ limit: 100 }),
  })

  const dismiss = useMutation({
    mutationFn: (id: number) => unmatchedApi.dismiss(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments-unmatched'] })
      void qc.invalidateQueries({ queryKey: ['payments-unmatched-count'] })
    },
  })

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">{t('paymentsPage.unmatchedHint')}</p>

      <div className="card overflow-x-auto">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Spinner className="h-5 w-5" />
          </div>
        ) : (data?.results.length ?? 0) === 0 ? (
          <EmptyState>{t('paymentsPage.unmatchedEmpty')}</EmptyState>
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>{t('paymentsPage.columnTxDate')}</th>
                <th>{t('paymentsPage.columnCounterparty')}</th>
                <th>{t('paymentsPage.columnVs')}</th>
                <th>{t('paymentsPage.columnReason')}</th>
                <th className="text-right">{t('paymentsPage.columnAmount')}</th>
                {!readOnly ? <th>{t('global.actions')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {data?.results.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDate(entry.transaction_date)}</td>
                  <td className="font-medium">
                    {entry.counterparty || t('paymentsPage.unknownCounterparty')}
                    {entry.message ? (
                      <span className="ml-1.5 text-slate-400">· {entry.message}</span>
                    ) : null}
                  </td>
                  <td className="font-mono text-slate-600">{entry.variable_symbol || '—'}</td>
                  <td className="text-amber-700">{entry.reason_label}</td>
                  <td className="text-right tabular-nums">{formatMoney(entry.amount)}</td>
                  {!readOnly ? (
                    <td>
                      <div className="flex gap-0.5">
                        <button
                          type="button"
                          className="btn-ghost p-1"
                          title={t('paymentsPage.match')}
                          onClick={() => setMatching(entry)}
                        >
                          <Check size={14} />
                        </button>
                        <button
                          type="button"
                          className="btn-ghost p-1 text-slate-400"
                          title={t('paymentsPage.dismiss')}
                          disabled={dismiss.isPending}
                          onClick={() => {
                            if (window.confirm(t('paymentsPage.dismissConfirm'))) {
                              dismiss.mutate(entry.id)
                            }
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {matching ? (
        <MatchUnmatchedModal
          entry={matching}
          onClose={() => setMatching(null)}
        />
      ) : null}
    </div>
  )
}

function SourceBadge({ source }: { source: PaymentSource }) {
  const { t } = useTranslation()
  const bank = bankByCode(source)
  if (bank) {
    return (
      <span className={cn('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium', BANK_SOURCE_STYLE)}>
        <BankIcon bank={bank} size={14} />
        {t('paymentsPage.source.bank', { bank: bankLabel(bank) })}
      </span>
    )
  }
  return (
    <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', SOURCE_STYLE[source] ?? SOURCE_STYLE.manual)}>
      {t(`paymentsPage.source.${source}`)}
    </span>
  )
}
